import { HttpClient } from '@angular/common/http'
import { Injectable } from '@angular/core'
import * as R from 'ramda'
import { BehaviorSubject, Observable } from 'rxjs'
import { map, tap } from 'rxjs/operators'
import { reconstructFilters, reconstructType } from '../utils'

// name, code, depth, isParent(startswith !), icon name(selection status)
export type FilterItemDepth = [string, string, number, boolean, string]
export type FilterItem = [string, string]
export const maxYarnCount = 105

const MARKETS = {
  동대문종합시장: '종합',
  종합시장부근: '종합',
  청계천로: '종합',
  을지상가: '통일',
  통일시장: '통일',
  신설동시장: '신설',
  동화시장: '동화',
  광장시장: '광장',
}

const abbrMarket = (addr: string) => {
  return MARKETS[addr.split(' ')[0]]
}

/**
 * 단순 필터 선택 정보를 string으로 들고 있음
 */
export type TSelected = {
  types: string[]
  ingreds: string[]
  // lines: string[]
  materials: string[]
  patterns: string[]
}

export type TCurrent = {
  types: FilterItemDepth[]
  ingreds: FilterItemDepth[]
  materials: FilterItemDepth[]
  patterns: FilterItemDepth[]
}

export interface SelectedFilters {
  types: FilterItem[]
  ingreds: FilterItem[]
  materials: FilterItem[]
  patterns: FilterItem[]
  lines: [number, number]
}

export interface CatsMaps {
  types: { [key: string]: FilterItem }
  ingreds: { [key: string]: FilterItem }
  // lines: { [key: string]: string[] }
  materials: { [key: string]: FilterItem }
  patterns: { [key: string]: FilterItem }
}

interface RawCategory {
  wovenTypes: FilterItem[]
  knitTypes: FilterItem[]
  etcTypes: FilterItem[]
  leatherTypes: FilterItem[]
  furTypes: FilterItem[]
  velvetTypes: FilterItem[]
  meshTypes: FilterItem[]
  ingreds: FilterItem[]
  materials: FilterItem[]
  patterns: FilterItem[]
}

export interface CategoryDepth {
  types: {
    wovenTypes: FilterItemDepth[]
    knitTypes: FilterItemDepth[]
    etcTypes: FilterItemDepth[]
    leatherTypes: FilterItemDepth[]
    furTypes: FilterItemDepth[]
    velvetTypes: FilterItemDepth[]
    meshTypes: FilterItemDepth[]
  }
  ingreds: FilterItemDepth[]
  materials: FilterItemDepth[]
  patterns: FilterItemDepth[]
}

// interface Category {
//   types: {
//     wovenTypes: FilterItem[]
//     knitTypes: FilterItem[]
//     etcTypes: FilterItem[]
//   }
//   ingreds: FilterItem[]
//   materials: FilterItem[]
//   patterns: FilterItem[]
// }

//
// =========== for Fabric DATA =============
//

interface Ingred {
  ingred: string
  rate: number
}

interface FabricProperty {
  id: number
  name: string
  type: string
  subtype: string
  ingreds: Ingred[]
  materials: string[]
  patterns: string[]
  cuttableWidth: number
  width: number
  weightMin: number
  weightMax: number
  unitPriceMin: number
  unitPriceMax: number
}

interface Store {
  id: number
  name: string
  phone: string
  description: string
  searchAddress: string
}

export interface Fabric {
  id: number
  name: string
  description: string
  // information
  numberOfColors: number
  followCount: number
  unitPriceMin: number
  unitPriceMax: number
  // images/video
  videos: { path: string }[]
  images: string[]
  colorsImages: string[]
  store: Store
  following: boolean
  wanted: boolean
  properties: FabricProperty[]
  updatedAt: Date
  //
  operating: boolean
}

export interface FabricsResp {
  count: number
  latestUpdatedAt?: Date
  latestUpdatedAtStr?: Date
  items: Fabric[]
}

export const getEmptyFilters = (): SelectedFilters => ({
  types: [],
  ingreds: [],
  materials: [],
  patterns: [],
  lines: [0, maxYarnCount],
})

const arrayToObject = R.reduce(
  (a: { [key: string]: FilterItem }, b: FilterItem) => ({
    ...a,
    [b[1]]: b,
  }),
  {},
)

const generateFilterMaps = (types: RawCategory): CatsMaps => ({
  types: arrayToObject(
    types.wovenTypes
      .concat(types.knitTypes)
      .concat(types.etcTypes)
      .concat(types.leatherTypes)
      .concat(types.furTypes)
      .concat(types.velvetTypes)
      .concat(types.meshTypes),
  ),
  ingreds: arrayToObject(types.ingreds),
  // lines: arrayToObject(types.lines),
  materials: arrayToObject(types.materials),
  patterns: arrayToObject(types.patterns),
})

@Injectable({
  providedIn: 'root',
})
export class FabricService {
  all$ = new BehaviorSubject<Fabric[]>([])
  scrap$ = new BehaviorSubject<Fabric[]>([])
  searchResult$ = new BehaviorSubject<Fabric[]>([])

  static cats: CategoryDepth
  static catsMaps: CatsMaps
  static banners: any

  public followedQueue$ = new BehaviorSubject(undefined)
  public scrolledStatus$ = new BehaviorSubject(2)

  constructor(private http: HttpClient) { }

  public static getFilterTarget() {
    return [
      [FabricService.cats.types.wovenTypes, '조직', 'types'],
      [FabricService.cats.types.knitTypes, '조직', 'types'],
      [FabricService.cats.types.etcTypes, '조직', 'types'],
      [FabricService.cats.types.leatherTypes, '조직', 'types'],
      [FabricService.cats.types.furTypes, '조직', 'types'],
      [FabricService.cats.types.velvetTypes, '조직', 'types'],
      [FabricService.cats.types.meshTypes, '조직', 'types'],
      [FabricService.cats.ingreds, '성분', 'ingreds'],
      [FabricService.cats.materials, '특성', 'materials'],
      [FabricService.cats.patterns, '패턴', 'patterns'],
    ]
  }

  public fetchFabricCats() {
    return {
      cats: FabricService.cats,
      catsMaps: FabricService.catsMaps,
    }
  }

  public fetchFabricCount(): Observable<{ count: number }> {
    return this.http.get<FabricsResp>('/api/v2/fabrics/count')
  }

  public fetchFabricByFabricIds(swatchIds = []) {
    return this.http.get<FabricsResp>(`/api/v1/fabrics?swatchIds=${R.join(',', swatchIds)}`).pipe(
      map(resp => ({
        ...resp,
        items: R.map(
          (vv: any) => ({
            ...vv,
            images: R.map((vvv: string) => vvv.replace('.jpg', '-s.webp'), vv.images),
          }),
          resp.items,
        ),
      })),
    )
  }

  public fetchFabrics(segment: string, skip: number, take = 30): Observable<FabricsResp> {
    return this.http
      .get<FabricsResp>(`/api/v1/fabrics?take=${take}&skip=${skip}&segment=${segment}`)
      .pipe(
        map(resp => ({
          ...resp,
          items: R.map(
            (vv: any) => ({
              ...vv,
              images: R.map((vvv: string) => vvv.replace('.jpg', '-s.webp'), vv.images),
              market: abbrMarket(vv.store.searchAddress),
            }),
            resp.items,
          ),
        })),
      )
  }

  /**
   * 키워드 검색결과 메인화면용
   *
   * @param q
   * @param skip
   * @returns
   */
  public fetchCurationFabricsV1(q: string, skip: number): Observable<FabricsResp> {
    return this.http.get(`/api/v2/search-fabric?q=${q}&skip=${skip}&take=20`).pipe(
      map(({ count, items, latestUpdatedAt }: FabricsResp) => ({
        count,
        items,
        latestUpdatedAt,
      })),
    )
  }

  public fetchFabric(id: number): Observable<Fabric> {
    return this.http.get<Fabric>(`/api/v1/fabrics/${id}`).pipe(
      map(resp => ({
        ...resp,
        description: R.trim(resp.description || ''),
        images: R.map((vv: string) => vv.replace('.jpg', '-l.webp'), resp.images),
      })),
    )
  }

  public toggleFollow(fabric: Fabric) {
    return this.http.post(`/api/v1/fabrics/${fabric.id}/toggle-follow`, {}).pipe(
      tap(() => {
        fabric.followCount = fabric.followCount + (fabric.following ? -1 : 1)
        fabric.following = !fabric.following
        this.followedQueue$.next(fabric)
      }),
    )
  }

  public toggleWanted(fabric: Fabric) {
    const f = {
      ...fabric,
      wanted: !fabric.wanted,
    }
    // this.wantedQueue$.next(f)
    return this.http.post(`/api/v1/fabrics/${f.id}/toggle-wanted`, {}).pipe(map(() => f))
  }

  public getWholeTypes() {
    const { wovenTypes, knitTypes, etcTypes, leatherTypes, furTypes, velvetTypes, meshTypes } =
      FabricService.cats.types
    return wovenTypes
      .concat(knitTypes)
      .concat(etcTypes)
      .concat(leatherTypes)
      .concat(furTypes)
      .concat(velvetTypes)
      .concat(meshTypes)
  }

  public init() {
    return Promise.all([
      new Promise(resolve => {
        this.http.get('/api/v1/common/banners').subscribe({
          next: banners => {
            FabricService.banners = banners
            resolve(true)
          },
          error: () => resolve(false),
        })
      }),
      new Promise(resolve => {
        this.http
          .get<RawCategory>('/api/v1/fabric-category')
          .pipe(
            map((records: RawCategory) => {
              // 필터 단계 조정
              // 카테고리 제목으로 쓰이는것들은 이름앞에 !를 붙여 구분
              // 예) 능직, 능직-헤링본, 능직-기타 중 능직은 카테고리 제목이므로 !W-TWILL과 같이 저장
              const wovenTypes = reconstructType(records.wovenTypes)
              const knitTypes = reconstructType(records.knitTypes)
              const etcTypes = reconstructType(records.etcTypes)
              const leatherTypes = reconstructType(records.leatherTypes || [])
              const furTypes = reconstructType(records.furTypes || [])
              const velvetTypes = reconstructType(records.velvetTypes || [])
              const meshTypes = reconstructType(records.meshTypes || [])
              const ingreds = reconstructFilters(records.ingreds)
              const materials = reconstructFilters(records.materials)
              const patterns = reconstructFilters(records.patterns)

              FabricService.cats = {
                types: {
                  wovenTypes,
                  knitTypes,
                  etcTypes,
                  leatherTypes,
                  furTypes,
                  velvetTypes,
                  meshTypes,
                },
                ingreds,
                materials,
                patterns,
              }

              // 코드를 키로 하는 딕셔너리로 정리
              FabricService.catsMaps = generateFilterMaps({
                wovenTypes: wovenTypes as unknown as FilterItem[],
                knitTypes: knitTypes as unknown as FilterItem[],
                etcTypes: etcTypes as unknown as FilterItem[],
                leatherTypes: leatherTypes as unknown as FilterItem[],
                furTypes: furTypes as unknown as FilterItem[],
                velvetTypes: velvetTypes as unknown as FilterItem[],
                meshTypes: meshTypes as unknown as FilterItem[],
                ingreds: ingreds as unknown as FilterItem[],
                materials: materials as unknown as FilterItem[],
                patterns: patterns as unknown as FilterItem[],
              })

              return {
                cats: FabricService.cats,
                catsMaps: FabricService.catsMaps,
              }
            }),
          )
          .subscribe({
            next: () => resolve(true),
            error: () => resolve(false),
          })
      }),
    ])
  }

  public search(q = '', skip = 0, take = 20) {
    return this.http
      .get<FabricsResp>(
        `/api/v2/search-fabric?q=${encodeURIComponent(q)}&skip=${skip}&take=${take}`,
      )
      .pipe(
        map(resp => ({
          ...resp,
          items: R.map(
            (vv: any) => ({
              ...vv,
              images: R.map((vvv: string) => vvv.replace('.jpg', '-s.webp'), vv.images),
              market: abbrMarket(vv.store.searchAddress),
            }),
            resp.items,
          ),
        })),
      )
  }
}
